/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOOLTIP_UPDATES_BROADCAST_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added tooltips to 11 broadcast fields.
 * Extracted helper functions to broadcastCardHelpers.ts.
 *
 * File Size Check:
 * - Current: 231 lines
 * - Removing helpers: -100 lines
 * - Adding tooltips: +25 lines
 * - Projected: 156 lines
 * - Decision: Proceed
 *
 * Typography Compliance:
 * - Utilities used: InfoTooltip component
 * - Tokens used: --color-brand-primary
 *
 * Related Files:
 * - client/src/constants/tooltips.ts
 * - client/src/components/ui/InfoTooltip.tsx
 * - client/src/components/broadcast/broadcastCardHelpers.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    TOOLTIP_UPDATES_IRIT_2026
 * Date:         2026-01-27
 * Modified By:  Cursor
 *
 * Description:
 * Added Irit's approved tooltips to 11 broadcast fields.
 * Extracted helper functions to broadcastCardHelpers.ts.
 *
 * File Size Check:
 * - Current: 231 lines
 * - Removing helpers: -100 lines
 * - Adding tooltips: +25 lines
 * - Projected: 156 lines
 * - Decision: Proceed
 *
 * Typography Compliance:
 * - Utilities used: InfoTooltip component
 * - Tokens used: --color-brand-primary (text-info)
 *
 * Related Files:
 * - client/src/constants/tooltips.ts
 * - client/src/components/ui/InfoTooltip.tsx
 * - client/src/components/broadcast/broadcastCardHelpers.ts
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_ACTION_BUTTONS_PHASE1_REVERT
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Reverted Phase 1 action button changes. BroadcastItemCard is not used in
 * "My Broadcasts" tab, so action buttons were removed. Buttons will be added
 * to DashboardBroadcastSummaryTable instead.
 *
 * File Size Check:
 * - Current: 264 lines
 * - Removing: ~76 lines
 * - Projected: 188 lines (back to original)
 * - Decision: Proceed
 *
 * Related Screens / Modules:
 * - client/src/features/dashboard/DashboardBroadcastSummaryTable.tsx (will add buttons here)
 */
/**
 * UAVTradeZone Modification Record
 *
 * Change ID:    BROADCAST_ACTION_BUTTONS_PHASE1
 * Date:         2025-01-02
 * Modified By:  Cursor
 *
 * Description:
 * Phase 1: Add hover state and action buttons to broadcast rows in "My Broadcasts".
 * Implements 1-second hover delay before showing buttons. Buttons perform console.log
 * only in this phase (no API calls yet).
 *
 * File Size Check:
 * - Current: 188 lines
 * - Adding: ~40 lines
 * - Projected: 228 lines
 * - Decision: Proceed (under 250-line limit)
 *
 * Related Screens / Modules:
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastActionButtons.tsx
 * - client/src/features/broadcast/pages/broadcasts-list/components/BroadcastActionButtons.module.scss
 */
import React from "react";
import { BroadcastGroup, BroadcastItem } from "../../interfaces/broadcast";
import { BROADCAST_TOOLTIPS } from "../../constants/tooltips";
import {
  getConditionBackgroundColor,
  getConditionTextColor,
  formatRelativeTime,
} from "./broadcastCardHelpers";
// [2026-02-13] CURRENCY_PHASE2: Centralized single-price display
import { formatCurrency } from "../../utils/priceDisplay";
import "./BroadcastItemCard.scss";

// Flattened broadcast item for display
export interface FlattenedBroadcastItem extends BroadcastItem {
  broadcastGroup: BroadcastGroup;
  companyName: string;
  userCountry?: string; // Country from the user who submitted the broadcast
  _id?: string; // For compatibility with generic grid components
}

export interface BroadcastItemCardProps {
  item: FlattenedBroadcastItem;
  onClick?: (item: FlattenedBroadcastItem) => void;
  displayMode?: "row";
}

export function BroadcastItemCard({
  item,
  onClick,
  displayMode = "row",
}: BroadcastItemCardProps) {
  const title = item.title || `${item.manufacturer} ${item.partNumber || item.model || ""}`.trim();
  const categories = item.categories && item.categories.length > 0 
    ? item.categories.join(", ")
    : "-";

  return (
    <div
      className="broadcast-item-card inv-row"
      onClick={(e) => {
        if (onClick) {
          e.preventDefault();
          onClick(item);
        }
      }}
    >
      {/* Column 1: WTS/WTB badge (spans 2 rows) */}
      <div className="broadcast-item-card__badge" title={BROADCAST_TOOLTIPS.TYPE}>
        <span
          className="broadcast-item-card__type-badge"
          style={{
            backgroundColor:
              item.broadcastGroup.type === "WTB"
                ? "var(--color-brand-primary)"
                : "var(--color-status-blue)",
            color: "var(--color-white)",
          }}
        >
          {item.broadcastGroup.type}
        </span>
      </div>

      {/* Column 2, Row 1: Product Title */}
      <div className="broadcast-item-card__title" title={BROADCAST_TOOLTIPS.TITLE}>
        {title}
      </div>

      {/* Column 3, Row 1: Category */}
      <div className="broadcast-item-card__category u-P3-14-medium" title={BROADCAST_TOOLTIPS.CATEGORY}>
        {categories}
      </div>

      {/* Column 4, Row 1: QTY */}
      <div className="broadcast-item-card__qty u-P3-14-medium" title={BROADCAST_TOOLTIPS.QUANTITY}>
        QTY: {item.quantity}
      </div>

      {/* Column 5, Row 1: Condition badge */}
      <div className="broadcast-item-card__condition" title={BROADCAST_TOOLTIPS.CONDITION}>
        <span
          className="broadcast-item-card__condition-badge"
          style={{
            backgroundColor: getConditionBackgroundColor(item.condition),
            color: getConditionTextColor(item.condition),
          }}
        >
          {item.condition || "-"}
        </span>
      </div>

      {/* Column 2, Row 2: Description */}
      <div
        className="broadcast-item-card__description u-P3-14-medium"
        title={item.description ? BROADCAST_TOOLTIPS.DESCRIPTION : undefined}
      >
        {item.description || ""}
      </div>

      {/* Column 3, Row 2: Company name */}
      <div className="broadcast-item-card__company u-Label-12-medium" title={BROADCAST_TOOLTIPS.COMPANY}>
        {item.companyName}
      </div>

      {/* Column 4, Row 2: Time posted */}
      <div className="broadcast-item-card__time u-Label-12-medium" title={BROADCAST_TOOLTIPS.TIME_POSTED}>
        {formatRelativeTime(item.broadcastGroup.createdAt)}
      </div>

      {/* Column 5, Row 2: Price - [2026-02-13] CURRENCY_PHASE2: Single price in user's currency */}
      <div className="broadcast-item-card__price u-P1-18-semi-bold" title={BROADCAST_TOOLTIPS.PRICE}>
        {formatCurrency(item.price, item.currency, (item as any).convertedPrice, (item as any).displayCurrency) || "N/A"}
      </div>
    </div>
  );
}
